import { inject, Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { ChatService } from '../../services/chat.service';
import { Message, MessageConnection } from '../../models/chat.models';

import {
    LoadFiles,
    LoadFilesSuccess,
    LoadFilesError,
    LoadMoreFiles,
    LoadMoreFilesSuccess,
    LoadMoreFilesError,
    ClearFilesForChannel,
} from '../actions/file.actions';
import { CurrentUserModel } from '@shared/common/models';

export interface FileStateModel {
    byChannel: {
        [channelId: string]: {
            items: Message[];
            nextToken?: string;
            loading: boolean;
            error: unknown;
            lastUpdated: number;
        };
    };
}

@State<FileStateModel>({
    name: 'files',
    defaults: {
        byChannel: {},
    },
})
@Injectable()
export class FileState {
    private currentUser = inject(CurrentUserModel);
    constructor(private chatService: ChatService) {}

    @Selector()
    static getFiles(state: FileStateModel | undefined) {
        return (channelId: string): Message[] => state?.byChannel?.[channelId]?.items ?? [];
    }

    @Selector()
    static getNextToken(state: FileStateModel | undefined) {
        return (channelId: string): string | undefined => state?.byChannel?.[channelId]?.nextToken;
    }

    @Selector()
    static isLoading(state: FileStateModel | undefined) {
        return (channelId: string): boolean => !!state?.byChannel?.[channelId]?.loading;
    }

    @Action(LoadFiles)
    loadFiles(ctx: StateContext<FileStateModel>, action: LoadFiles) {
        this.ensureChannel(ctx, action.channelId);
        this.patchChannel(ctx, action.channelId, { loading: true, error: null });

        return this.chatService.getFiles(action.channelId, action.limit, action.nextToken).pipe(
            tap((connection: MessageConnection) =>
                ctx.dispatch(new LoadFilesSuccess(action.channelId, connection, action.nextToken)),
            ),
            catchError((error) => {
                ctx.dispatch(new LoadFilesError(action.channelId, error));
                return of(error);
            }),
        );
    }

    @Action(LoadFilesSuccess)
    loadFilesSuccess(ctx: StateContext<FileStateModel>, action: LoadFilesSuccess) {
        const shouldAppend = !!action.requestNextToken;
        const state = ctx.getState();
        const slice = state.byChannel[action.channelId] ?? {
            items: [],
            nextToken: undefined,
            loading: false,
            error: null,
            lastUpdated: 0,
        };

        const baseItems = shouldAppend ? slice.items : [];
        const existingIds = new Set(baseItems.map((m) => m._id));
        const deduped = action.connection.items.filter((m) => !existingIds.has(m._id));

        const merged = [...baseItems, ...deduped];

        this.patchChannel(ctx, action.channelId, {
            items: merged,
            nextToken: action.connection.nextToken,
            loading: false,
            error: null,
            lastUpdated: Date.now(),
        });
    }

    @Action(LoadFilesError)
    loadFilesError(ctx: StateContext<FileStateModel>, action: LoadFilesError) {
        this.patchChannel(ctx, action.channelId, { loading: false, error: action.error });
    }

    @Action(LoadMoreFiles)
    loadMoreFiles(ctx: StateContext<FileStateModel>, action: LoadMoreFiles) {
        const state = ctx.getState();
        const slice = state.byChannel[action.channelId];
        const requestNextToken = slice?.nextToken;

        // Gate if no more pages known
        if (!requestNextToken && requestNextToken !== '') return;

        this.patchChannel(ctx, action.channelId, { loading: true, error: null });

        return this.chatService.getFiles(action.channelId, action.limit, requestNextToken).pipe(
            tap((connection) =>
                ctx.dispatch(
                    new LoadMoreFilesSuccess(action.channelId, connection, requestNextToken),
                ),
            ),
            catchError((error) => {
                ctx.dispatch(new LoadMoreFilesError(action.channelId, error));
                return of(error);
            }),
        );
    }

    @Action(LoadMoreFilesSuccess)
    loadMoreFilesSuccess(ctx: StateContext<FileStateModel>, action: LoadMoreFilesSuccess) {
        const state = ctx.getState();
        const slice = state.byChannel[action.channelId] ?? { items: [] as Message[] };
        const existingIds = new Set(slice.items.map((m) => m._id));
        const newItems = action.connection.items.filter((m) => !existingIds.has(m._id));

        const merged = [...slice.items, ...newItems];

        this.patchChannel(ctx, action.channelId, {
            items: merged,
            nextToken: action.connection.nextToken,
            loading: false,
            error: null,
            lastUpdated: Date.now(),
        });
    }

    @Action(LoadMoreFilesError)
    loadMoreFilesError(ctx: StateContext<FileStateModel>, action: LoadMoreFilesError) {
        this.patchChannel(ctx, action.channelId, { loading: false, error: action.error });
    }

    @Action(ClearFilesForChannel)
    clearFilesForChannel(ctx: StateContext<FileStateModel>, action: ClearFilesForChannel) {
        const state = ctx.getState();
        const { [action.channelId]: _removed, ...rest } = state.byChannel;
        ctx.patchState({ byChannel: rest });
    }

    // Helpers
    private ensureChannel(ctx: StateContext<FileStateModel>, channelId: string) {
        const state = ctx.getState();
        if (!state.byChannel[channelId]) {
            ctx.patchState({
                byChannel: {
                    ...state.byChannel,
                    [channelId]: {
                        items: [],
                        nextToken: undefined,
                        loading: false,
                        error: null,
                        lastUpdated: 0,
                    },
                },
            });
        }
    }

    private patchChannel(
        ctx: StateContext<FileStateModel>,
        channelId: string,
        patch: Partial<FileStateModel['byChannel'][string]>,
    ) {
        const state = ctx.getState();
        const slice = state.byChannel[channelId] ?? {
            items: [],
            nextToken: undefined,
            loading: false,
            error: null,
            lastUpdated: 0,
        };
        ctx.patchState({
            byChannel: {
                ...state.byChannel,
                [channelId]: { ...slice, ...patch },
            },
        });
    }
}
